import * as React from 'react';
import { useDroppable } from '@dnd-kit/core';
import { TaskCard } from './task-card.js';
import type { Task } from '../lib/tasks-api.js';
import type { TaskColumnView } from '../lib/task-board.js';

export interface TaskColumnProps {
  readonly column: TaskColumnView;
  readonly items: readonly Task[];
  readonly now: Date;
  readonly onOpen: (id: string) => void;
  /** undefined = žádný přesun neprobíhá; jinak true/false podle `allowedTransitions` tažené karty. */
  readonly dropAllowed: boolean | undefined;
  /** Sloupec mimo konfiguraci serveru (viz `groupTasksByColumn`) — nesmí vypadat jako běžný. */
  readonly unknown?: boolean;
  /** Krok TAB1 bod 4: věta pod sloupcem (předloha: „Sem padá, co čeká na klienta…“). */
  readonly hint?: string | undefined;
  /** Krok TAB1 bod 4: odkaz pod sloupcem „Hotovo dnes“ („Starší hotové (N)“). */
  readonly footer?: React.ReactNode;
  /** Jméno zakázky úkolu a cesta na její detail (předloha: štítek s ikonou místa). */
  readonly caseName: (task: Task) => string | null;
  readonly onOpenCase: (task: Task) => (() => void) | null;
  readonly ownerName: (task: Task) => string | null;
  /** JEDNA kontextová akce karty — co to je, rozhoduje tabule, ne karta. */
  readonly action: (task: Task) => { readonly label: string; readonly onAction: () => void } | null;
}

/**
 * Sloupec tabule úkolů. `dropAllowed` se řídí `allowedTransitions` z API
 * (viz `task-card.tsx`) — nepovolený cíl se v UI vůbec nenabídne, server ho
 * stejně odmítne (422).
 *
 * Krok TAB1 bod 4 přidal `hint` a `footer`: předloha má pod sloupcem „Čeká na
 * druhé“ vysvětlující větu a pod „Hotovo dnes“ odkaz na starší hotové.
 */
export function TaskColumn({
  column,
  items,
  now,
  onOpen,
  dropAllowed,
  unknown = false,
  hint,
  footer,
  caseName,
  onOpenCase,
  ownerName,
  action,
}: TaskColumnProps): React.JSX.Element {
  const { setNodeRef } = useDroppable({ id: column.key });
  const bodyClass =
    dropAllowed === undefined
      ? 'rh-a2-kcol__body'
      : `rh-a2-kcol__body ${dropAllowed ? 'rh-kanban-column__body--drop-ok' : 'rh-kanban-column__body--drop-no'}`;

  return (
    <div className={`rh-a2-kcol${unknown ? ' rh-a2-kcol--unknown' : ''}`} data-testid={`task-column-${column.key}`}>
      <div className="rh-a2-kcol__head">
        <span className={`rh-a2-dot rh-a2-dot--${column.key}`} aria-hidden="true" />
        <span>{column.label}</span>
        <span className="rh-a2-count" data-testid={`task-column-count-${column.key}`}>
          {items.length}
        </span>
      </div>
      <div ref={setNodeRef} className={bodyClass}>
        {items.map((item) => (
          <TaskCard
            key={item.id}
            item={item}
            now={now}
            onOpen={onOpen}
            caseName={caseName(item)}
            ownerName={ownerName(item)}
            action={action(item)}
            onOpenCase={onOpenCase(item)}
            done={item.status === 'done'}
          />
        ))}
      </div>
      {hint !== undefined ? (
        <p className="rh-a2-kcol__hint" data-testid={`task-column-hint-${column.key}`}>
          {hint}
        </p>
      ) : null}
      {footer}
    </div>
  );
}
